<template>
  <div class="apply-form__section apply-form__personal-data">
    <h2 v-if="!shortForm">{{ $t('application.personalData') }}</h2>
    <h4 v-else>{{ $t('application.personalData') }}</h4>
    <v-row>
      <v-col cols="12">
        <div class="text--muted text-sm flex-grow-1">
          {{ $t('application.mandatoryFieldInfo') }}
        </div>
      </v-col>
    </v-row>
    <ValidationObserver>
      <v-row>
        <v-col cols="12" :md="shortForm ? 12 : 6">
          <ValidationProvider
            v-slot="{ errors }"
            name="email"
            rules="required|email"
            ref="emailProvider"
            :custom-messages="{
              required: $t('validation.emailInvalid'),
              email: $t('validation.emailInvalid')
            }"
            mode="eager"
          >
            <v-text-field
              v-model="application.email"
              outlined
              flat
              hide-details="auto"
              name="email"
              type="email"
              autocomplete="email"
              :label="`${$t('application.emailAddress')}*`"
              :error-messages="errors"
              @change="handleChange"
            />
          </ValidationProvider>
        </v-col>

        <v-col cols="12" :md="shortForm ? 12 : 6">
          <ValidationProvider
            v-slot="{ errors }"
            name="confirmEmail"
            ref="confirmEmailProvider"
            :rules="confirmEmailRules"
            :custom-messages="{
              required: $t('validation.emailInvalid'),
              email: $t('validation.emailInvalid')
            }"
            mode="eager"
          >
            <v-text-field
              flat
              v-model="application.confirmEmail"
              outlined
              hide-details="auto"
              name="confirmEmail"
              type="email"
              :label="`${$t('application.confirmEmail')}*`"
              :error-messages="errors"
              @paste.prevent
              @change="handleChange"
            />
          </ValidationProvider>
        </v-col>

        <v-col cols="12" :md="shortForm ? 12 : 6">
          <ValidationProvider
            v-slot="{ errors }"
            name="salutation"
            rules="required"
            :custom-messages="{
              required: $t('validation.salutationIsMandatory')
            }"
          >
            <v-select
              v-model="application.salutationId"
              :items="genderOptions"
              outlined
              flat
              hide-details="auto"
              name="salutation"
              :label="`${$t('application.salutation')}*`"
              item-text="label"
              item-value="value"
              :error-messages="errors"
              :menu-props="{ bottom: true, offsetY: true }"
              @change="handleChange"
            ></v-select>
          </ValidationProvider>
        </v-col>

        <v-col cols="12" :md="shortForm ? 12 : 6">
          <v-select
            v-model="application.title"
            name="academicTitle"
            :items="titleOptions"
            :label="`${$t('application.title')}`"
            :menu-props="{ bottom: true, offsetY: true }"
            outlined
            flat
            clearable
            hide-details="auto"
            item-text="label"
            item-value="value"
            @change="handleChange"
          ></v-select>
        </v-col>
      </v-row>

      <v-row :class="!shortForm ? 'border-bot' : ''">
        <v-col cols="12" md="6">
          <ValidationProvider
            v-slot="{ errors }"
            name="firstName"
            rules="required"
            :custom-messages="{
              required: $t('validation.firstNameIsMandatory')
            }"
          >
            <v-text-field
              v-model="application.firstName"
              outlined
              flat
              hide-details="auto"
              name="firstName"
              autocomplete="given-name"
              :label="`${$t('application.firstName')}*`"
              :error-messages="errors"
              @change="handleChange"
            />
          </ValidationProvider>
        </v-col>

        <v-col cols="12" md="6">
          <ValidationProvider
            v-slot="{ errors }"
            name="lastName"
            rules="required"
            :custom-messages="{
              required: $t('validation.lastNameIsMandatory')
            }"
          >
            <v-text-field
              v-model="application.lastName"
              name="lastName"
              outlined
              flat
              hide-details="auto"
              autocomplete="family-name"
              :label="`${$t('application.lastName')}*`"
              :error-messages="errors"
              @change="handleChange"
            />
          </ValidationProvider>
        </v-col>
      </v-row>

      <template v-if="shortForm">
        <v-row v-if="disabilityField.isActive">
          <v-col cols="12">
            <v-select
              v-model="disability.value"
              clearable
              outlined
              flat
              hide-details="auto"
              item-text="label"
              item-value="value"
              :label="disabilityField.label"
              :items="disabilityField.items"
              :menu-props="{ bottom: true, offsetY: true }"
              @change="handleChange"
            ></v-select>
          </v-col>
        </v-row>

        <v-row v-if="isBasicsFieldEnabled(basicsFields.RequestedSalary)">
          <v-col cols="12">
            <salary-field v-model="salary.value" :item="salary"> </salary-field>
          </v-col>
        </v-row>

        <basic-data-group
          v-for="groupIndex in basicsGroups"
          v-model="internalBasicsItems"
          :group-index="groupIndex"
          :key="`basics_data_group_${groupIndex}`"
          :short-apply="shortForm"
        ></basic-data-group>
      </template>

      <template v-else>
        <basic-data-group
          v-for="groupIndex in basicsGroups"
          v-model="basicsItems"
          :group-index="groupIndex"
          :key="`basics_data_group_${groupIndex}`"
          :short-apply="shortForm"
        ></basic-data-group>
      </template>
    </ValidationObserver>
  </div>
</template>

<script>
import basicDataGroup from './basic-data/basic-data-group.vue';

import basicsFieldsEnums from '@/enum/application-basics-fields.js';
import validationModeEnums from '@/enum/field-validation-mode.js';

import salaryField from '@/components/application/basic-data/salary-field.vue';

import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';
import { required, email } from 'vee-validate/dist/rules';

import i18n from '@/i18n';

extend('required', {
  ...required,
  message: i18n.t('validation.fieldIsMandatory')
});

extend('email', {
  ...email,
  message: () => i18n.t('validation.emailInvalid')
});

extend('emailMatch', {
  params: ['target'],
  validate(value, { target }) {
    return value === target;
  },
  message: () => i18n.t('validation.emailNoMatch')
});

export default {
  props: {
    value: {
      type: Object,
      required: true
    },

    shortForm: {
      type: Boolean,
      required: false,
      default: false
    },

    joboffer: {
      type: Object,
      required: false,
      default: () => {}
    },

    basicsSettings: {
      type: Array,
      required: false,
      default: () => []
    },

    internal: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data: () => ({
    titleOptions: ['Dr.', 'Prof.', 'Prof. Dr.'],
    basicsItems: [],
    internalBasicsItems: [],
    salaryRange: [30, 50]
  }),

  watch: {
    basicsSettings() {
      this.basicsItems = this.basicsSettings;
      this.internalBasicsItems = this.basicsSettings.filter(
        (x) => this.internal && x.internal
      );
    },

    salaryRange() {
      const minSalary = (this.salaryRange[0] * 1000).toLocaleString() + '€';
      const maxSalary = (this.salaryRange[1] * 1000).toLocaleString() + '€';

      if (minSalary === maxSalary) {
        this.salary = maxSalary;
      } else {
        this.salary.value = `${minSalary} - ${maxSalary}`;
      }
    }
  },

  computed: {
    application: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },

    basicsFields() {
      return basicsFieldsEnums.ApplicationBasicsFields;
    },

    basicsGroups() {
      const indexes = this.basicsItems
        .map((x) => x.groupIndex)
        .sort((a, b) => a - b);

      return [...new Set(indexes)];
    },

    genderOptions() {
      return [
        { label: this.$t('application.salutations.male'), value: '1' },
        { label: this.$t('application.salutations.female'), value: '2' },
        { label: this.$t('application.salutations.others'), value: '4' }
      ];
    },

    confirmEmailRules() {
      return `required|email|emailMatch:${this.application.email}`;
    },

    fieldValidationMode() {
      return validationModeEnums.FieldValidationModes;
    },

    emailRule() {
      return [
        (v) => !!v || this.$t('validation.fieldIsMandatory'),
        (v) => /.+@.+\..+/.test(v) || this.$t('validation.emailInvalid')
      ];
    },

    disability() {
      return this.getBasicsField(this.basicsFields.Disability);
    },

    salary() {
      return this.getBasicsField(this.basicsFields.RequestedSalary);
    },

    disabilityField() {
      const field = this.basicsSettings.find(
        (x) => x.dataDefinitionId === this.basicsFields.Disability
      );

      const data = {
        label: '',
        isActive: false,
        items: []
      };

      if (field) {
        data.isActive =
          field.fieldValidationMode !== this.fieldValidationMode.NotPrompted;

        data.label = this.localize(field.label);
        data.items = field.options
          .map((x) => {
            return {
              value: x.value,
              label: this.localize(x.label)
            };
          })
          .sort((a, b) => a.value - b.value);
      }

      return data;
    },

    notEmptyRule() {
      return [
        (value) => {
          if (!value || value.toString().trim() === '') {
            return this.$t('validation.fieldIsMandatory');
          }

          return true;
        }
      ];
    }
  },

  mounted() {
    this.basicsItems = this.basicsSettings;
    this.internalBasicsItems = this.basicsSettings.filter(
      (x) => this.internal && x.internal
    );
  },

  methods: {
    resetEmailValidation() {
      this.$refs.emailProvider?.reset();
      this.$refs.confirmEmailProvider?.reset();
    },

    async validateEmails() {
      const emailProvider = this.$refs.emailProvider;
      const confirmEmailProvider = this.$refs.confirmEmailProvider;

      if (!emailProvider || !confirmEmailProvider) {
        return true;
      }

      const emailResult = await emailProvider.validate();
      const confirmEmailResult = await confirmEmailProvider.validate();
      return emailResult.valid && confirmEmailResult.valid;
    },

    isBasicsFieldEnabled(candDataId) {
      const field = this.getBasicsField(candDataId);
      if (!field) {
        return false;
      }

      return field.fieldValidationMode !== this.fieldValidationMode.NotPrompted;
    },

    getBasicsField(candDataId) {
      return this.basicsSettings.find((x) => x.dataDefinitionId === candDataId);
    },

    handleChange() {
      this.$emit('change');
    }
  },

  components: {
    basicDataGroup,
    salaryField,
    ValidationObserver,
    ValidationProvider
  }
};
</script>
